<script setup lang="ts">
import { computed } from 'vue'
import CallPanel from '@/components/CallPanel.vue'
import CaveatBox from '@/components/CaveatBox.vue'
import CheckRow from '@/components/CheckRow.vue'
import IntroPanel from '@/components/IntroPanel.vue'
import ManualChecklist from '@/components/ManualChecklist.vue'
import ResultPanel from '@/components/ResultPanel.vue'
import StatusBanner from '@/components/StatusBanner.vue'
import * as da from '@/strings/da'
import { useConnectionCheck } from '@/composables/useConnectionCheck'
import { BRAND, BRAND_LOGO } from '@/lib/brand'
import { TCP_TONE, UDP_TONE } from '@/lib/presentation'

const {
	phase, running, started, tcpChecks, udpStatus, udpResult, publicIp, overall,
	apiFailure, callFailure, callConnected, platformResult, platformStillPending,
	browserMeasurement, browserMediaMs, browserVerdict, effectiveVerdict,
	remoteLevel, toneLevel, remoteAudibleMs, remoteElapsedMs, tonePeak,
	completedSteps, totalSteps, progress, secondsLeft, audioEl, run, stop
} = useConnectionCheck()

/** The call phases during which the visitor has something to do. */
const inCall = computed(() => phase.value === 'ringing' || phase.value === 'in-call')

const phaseLabel = computed(() => {
	const current = phase.value
	if (current === 'idle' || current === 'result') return da.banner.run.headline
	return da.phase[current]
})

const showResult = computed(() => phase.value === 'result')

const showCaveat = computed(() =>
	showResult.value && (overall.value === 'ok' || overall.value === 'warn'))

/** The measured facts behind the UDP verdict, for whoever has to act on it. */
const udpMeta = computed(() => {
	const result = udpResult.value
	if (!result || result.mappedPorts.length === 0) return null

	const source = result.via === 'platform' ? BRAND : 'offentlig STUN'
	const nat = result.natType === 'symmetric' ? 'symmetrisk' : 'cone (normal)'
	const ports = result.mappedPorts.length === 1
		? `mappet port ${result.mappedPorts[0]}`
		: `mappede porte ${result.mappedPorts.join(', ')}`

	return `Svar fra ${source} · NAT-type: ${nat} · ${ports}`
})
</script>

<template>
	<main class="page">
		<div class="card tcx-card">
			<header class="header">
				<img
					v-if="BRAND_LOGO"
					class="logo-image"
					:src="BRAND_LOGO.src"
					:width="BRAND_LOGO.width"
					:height="BRAND_LOGO.height"
					:alt="BRAND"
				>
				<p v-else class="logo">{{ da.page.logo }}</p>
				<h1>{{ da.page.title }}</h1>
				<p class="lede">{{ da.page.intro }}</p>
			</header>

			<IntroPanel v-if="!started" :disabled="running" @start="run" />

			<template v-else>
				<StatusBanner
					:status="overall"
					:running="running"
					:public-ip="publicIp"
					:phase-label="phaseLabel"
					:completed-steps="completedSteps"
					:total-steps="totalSteps"
					:progress="progress"
					:seconds-left="secondsLeft"
				/>

				<CallPanel
					v-if="inCall"
					:remote-level="remoteLevel"
					:tone-level="toneLevel"
					:remote-audible-ms="remoteAudibleMs"
					:remote-elapsed-ms="remoteElapsedMs"
					@stop="stop"
				/>

				<section class="checks">
					<CheckRow
						v-for="row in tcpChecks"
						:key="row.host"
						:title="da.firewall.tcpRow(row.label)"
						:tone="TCP_TONE[row.status]"
						:status-label="da.tcpLabel[row.status]"
						:pending="row.status === 'pending'"
					/>

					<CheckRow
						:title="da.firewall.udpRow"
						:tone="UDP_TONE[udpStatus]"
						:status-label="da.udpLabel[udpStatus]"
						:pending="udpStatus === 'pending'"
						:detail="da.udpDetail[udpStatus]"
					>
						<template v-if="udpMeta" #meta>{{ udpMeta }}</template>
					</CheckRow>
				</section>

				<ResultPanel
					v-if="showResult"
					:api-failure="apiFailure"
					:call-failure="callFailure"
					:connected="callConnected"
					:verdict="effectiveVerdict"
					:worse-direction="browserVerdict?.worseDirection ?? null"
					:platform="platformResult"
					:platform-still-pending="platformStillPending"
					:browser="browserMeasurement"
					:tone-peak="tonePeak"
					:browser-media-ms="browserMediaMs"
					:remote-audible-ms="remoteAudibleMs"
				/>

				<div v-if="showResult" class="actions">
					<button type="button" class="btn btn-tcx" @click="run">
						{{ da.page.rerun }}
					</button>
				</div>
			</template>

			<CaveatBox v-if="showCaveat" />

			<ManualChecklist />

			<footer class="footnote">{{ da.page.footnote }}</footer>
		</div>

		<!-- Must stay in the DOM: Chrome will not run an analyser on a remote stream that is not
		     attached to a media element, so this is measurement plumbing as well as playback. -->
		<audio ref="audioEl" autoplay playsinline :aria-label="da.page.audioLabel" />
	</main>
</template>

<style scoped>
.page {
	display: flex;
	justify-content: center;
}

.tcx-card {
	display: flex;
	flex-direction: column;
	gap: 26px;
	width: 100%;
	max-width: 720px;
	padding: 38px 40px 34px;
	border: 0;
	border-radius: var(--tcx-radius);
	background: var(--tcx-card);
	box-shadow: var(--tcx-shadow);
}

.header {
	display: flex;
	flex-direction: column;
}

.logo-image {
	display: block;
	margin: 0 0 16px;
	/* The attributes carry the real aspect ratio; this keeps it if the width is ever constrained. */
	height: auto;
	max-width: 100%;
}

.logo {
	margin: 0 0 10px;
	font-family: var(--tcx-font-display);
	font-weight: 700;
	font-size: 0.8125rem;
	letter-spacing: 0.16em;
	/* The name is derived from the hostname in sentence case; the wordmark sets it in caps. */
	text-transform: uppercase;
	color: var(--tcx-teal);
}

h1 {
	font-size: 2rem;
	font-weight: 700;
	line-height: 1.15;
}

.lede {
	margin: 10px 0 0;
	max-width: 60ch;
	font-size: 0.9375rem;
	color: var(--tcx-muted);
	text-wrap: pretty;
}

.checks {
	border-top: 1px solid var(--tcx-line);
}

.actions {
	display: flex;
}

.footnote {
	padding-top: 20px;
	border-top: 1px solid var(--tcx-line);
	max-width: 66ch;
	font-size: 0.8125rem;
	color: var(--tcx-muted);
}

@media (max-width: 560px) {
	.tcx-card {
		gap: 22px;
		padding: 26px 20px 24px;
		border-radius: 12px;
	}

	h1 {
		font-size: 1.5rem;
	}

	.actions .btn {
		width: 100%;
	}
}
</style>
